import { useState, useEffect, useRef } from "react";
import { Radio, Shield, Eye, EyeOff } from "lucide-react";
import { useAuth, BACKEND_URL } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Turnstile } from "@marsidev/react-turnstile";

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;

const LoginPage = () => {
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [stationName, setStationName] = useState("");
  const [plan, setPlan] = useState("starter");
  const [referralCode, setReferralCode] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState("");
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotMsg, setForgotMsg] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const turnstileRef = useRef(null);
  const { user, login, register, loginWithGoogle } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) navigate("/dashboard");
  }, [user, navigate]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get("ref");
    if (ref) setReferralCode(ref);
    const m = params.get("mode");
    if (m === "register") setMode("register");
  }, []);

  const switchMode = (m) => {
    setMode(m);
    setError("");
    setTurnstileToken("");
    turnstileRef.current?.reset();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (TURNSTILE_SITE_KEY && !turnstileToken) {
      setError("Please complete the verification");
      return;
    }
    setLoading(true);
    try {
      const t = turnstileToken === "skip" ? undefined : turnstileToken;
      if (mode === "login") {
        await login(email, password, t);
      } else {
        await register(name, email, password, plan, stationName, t, referralCode);
      }
      navigate("/dashboard");
    } catch (err) {
      setError(err.message);
      turnstileRef.current?.reset();
      setTurnstileToken("");
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotMsg("");
    setForgotLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: forgotEmail }),
      });
      const data = await res.json();
      setForgotMsg(data.message || "Check your email for a reset link.");
    } catch {
      setForgotMsg("Something went wrong. Please try again.");
    } finally {
      setForgotLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex" data-testid="login-page">
      {/* Left — Brand */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden items-center justify-center">
        <div className="absolute inset-0 bg-gradient-to-br from-purple-900/40 via-[#0a0a0a] to-[#0a0a0a]" />
        <div className="absolute top-[20%] left-[15%] w-72 h-72 bg-purple-500/10 rounded-full blur-[120px]" />
        <div className="absolute bottom-[20%] right-[10%] w-56 h-56 bg-pink-500/8 rounded-full blur-[100px]" />

        {/* Radio wave rings */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 opacity-[0.04]">
          {[200, 300, 400, 500].map((s) => (
            <div key={s} className="absolute border border-white rounded-full" style={{ width: s, height: s, top: `calc(50% - ${s/2}px)`, left: `calc(50% - ${s/2}px)` }} />
          ))}
        </div>

        <div className="relative z-10 px-12 max-w-lg">
          <a href="/">
            <img src="/logo-header.png" alt="DJsandMCMedia" className="h-10 mb-12 opacity-70" />
          </a>
          <h1 className="text-5xl font-black text-white leading-tight mb-6">
            Own your<br />sound.<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400">Build your</span><br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400">channel.</span>
          </h1>
          <p className="text-white/30 text-sm">
            Built for DJs, artists, and creators.
          </p>
        </div>
      </div>

      {/* Right — Auth Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md relative">
          {/* Mobile logo */}
          <div className="lg:hidden flex justify-center mb-8">
            <a href="/">
              <img src="/logo-header.png" alt="DJsandMCMedia" className="h-9 opacity-70" />
            </a>
          </div>

          <h2 className="text-2xl font-bold text-white mb-1" data-testid="login-heading">
            {mode === "login" ? "Welcome back" : "Create your account"}
          </h2>
          <p className="text-white/40 text-sm mb-8">
            {mode === "login"
              ? "Sign in to manage your channel and content."
              : plan === "listener"
              ? "Follow stations, get notified when they go live, and more."
              : "Start your channel in minutes. No credit card required."}
          </p>

          {/* Google */}
          <button
            onClick={loginWithGoogle}
            className="w-full flex items-center justify-center gap-3 bg-white hover:bg-gray-100 text-gray-800 font-medium py-3 rounded-xl transition mb-5"
            data-testid="google-signin-btn"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google
          </button>

          <div className="relative mb-5">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-white/10" /></div>
            <div className="relative flex justify-center text-sm"><span className="px-4 bg-[#0a0a0a] text-white/30">or</span></div>
          </div>

          {/* Error */}
          {error && (
            <div className="bg-red-500/15 border border-red-500/30 text-red-400 px-4 py-2.5 rounded-xl mb-4 text-sm" data-testid="auth-error">
              {error}
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === "register" && (
              <>
                <div>
                  <label className="block text-white/50 text-xs mb-1.5">Your Name</label>
                  <input type="text" value={name} onChange={(e) => setName(e.target.value)}
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder-white/20 focus:outline-none focus:border-purple-500 transition"
                    placeholder="John Doe" required data-testid="register-name-input" />
                </div>
                {plan !== "listener" && (
                  <div>
                    <label className="block text-white/50 text-xs mb-1.5">Channel Name</label>
                    <input type="text" value={stationName} onChange={(e) => setStationName(e.target.value)}
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder-white/20 focus:outline-none focus:border-purple-500 transition"
                      placeholder="My Awesome Radio" data-testid="register-station-input" />
                  </div>
                )}
              </>
            )}

            <div>
              <label className="block text-white/50 text-xs mb-1.5">Email</label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder-white/20 focus:outline-none focus:border-purple-500 transition"
                placeholder="you@example.com" required data-testid="auth-email-input" />
            </div>

            <div>
              <label className="block text-white/50 text-xs mb-1.5">Password</label>
              <div className="relative">
                <input type={showPw ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder-white/20 focus:outline-none focus:border-purple-500 transition pr-11"
                  placeholder="••••••••" required minLength={6} data-testid="auth-password-input" />
                <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-3 text-white/30 hover:text-white/60 transition">
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {mode === "login" && (
                <button
                  type="button"
                  onClick={() => { setForgotMode(true); setForgotEmail(email); setForgotMsg(""); }}
                  className="text-purple-400/70 hover:text-purple-300 text-xs transition mt-1"
                  data-testid="forgot-password-link"
                >
                  Forgot password?
                </button>
              )}
            </div>

            {mode === "register" && (
              <>
                <div>
                  <label className="block text-white/50 text-xs mb-1.5">Select Plan</label>
                    <div className="grid grid-cols-4 gap-2">
                    {[
                      { id: "starter", label: "Starter" },
                      { id: "professional", label: "Pro" },
                      { id: "premium", label: "Premium" },
                      { id: "byos", label: "BYOS Channel" },
                    ].map((p) => (
                      <button key={p.id} type="button" onClick={() => setPlan(p.id)}
                        className={`py-2.5 rounded-xl text-xs font-medium transition ${plan === p.id ? "bg-purple-500 text-white" : "bg-white/5 border border-white/10 text-white/60 hover:bg-white/10"}`}
                        data-testid={`plan-${p.id}-btn`}>
                        {p.label}
                      </button>
                    ))}
                  </div>
                  <button type="button" onClick={() => setPlan("listener")}
                    className={`w-full mt-2 py-2.5 rounded-xl text-xs font-medium transition ${plan === "listener" ? "bg-green-500/20 border border-green-500/40 text-green-400" : "bg-white/[0.02] border border-white/5 text-white/30 hover:text-white/50 hover:bg-white/5"}`}
                    data-testid="plan-listener-btn">
                    Just Listening — Free
                  </button>
                </div>
                <div>
                  <label className="block text-white/50 text-xs mb-1.5">Referral Code <span className="text-white/20">(optional)</span></label>
                  <input type="text" value={referralCode} onChange={(e) => setReferralCode(e.target.value)}
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder-white/20 focus:outline-none focus:border-purple-500 transition"
                    placeholder="Enter referral code" data-testid="register-referral-input" />
                </div>
              </>
            )}

            {TURNSTILE_SITE_KEY && (
              <div className="flex justify-center py-1" data-testid="turnstile-container">
                <Turnstile
                  ref={turnstileRef}
                  siteKey={TURNSTILE_SITE_KEY}
                  onSuccess={(token) => setTurnstileToken(token)}
                  onError={() => setTurnstileToken("skip")}
                  onExpire={() => { setTurnstileToken(""); turnstileRef.current?.reset(); }}
                  options={{ theme: "dark", size: "normal" }}
                />
              </div>
            )}

            <button type="submit" disabled={loading}
              className="w-full bg-purple-500 hover:bg-purple-600 text-white font-semibold py-3 rounded-xl transition disabled:opacity-50 mt-1"
              data-testid="auth-submit-btn">
              {loading ? "Please wait..." : mode === "login" ? "Sign In" : "Create Account"}
            </button>
          </form>

          {/* Forgot Password Inline */}
          {forgotMode && (
            <div className="absolute inset-0 bg-[#0a0a0a]/95 backdrop-blur-sm flex items-center justify-center z-50 rounded-2xl">
              <div className="w-full px-8">
                <h3 className="text-lg font-bold text-white mb-1" data-testid="forgot-heading">Reset Password</h3>
                <p className="text-white/40 text-xs mb-4">Enter your email and we'll send you a reset link.</p>
                {forgotMsg && (
                  <p className="text-green-400 text-xs mb-3 bg-green-500/10 border border-green-500/20 px-3 py-2 rounded-lg" data-testid="forgot-message">{forgotMsg}</p>
                )}
                <form onSubmit={handleForgotPassword} className="space-y-3">
                  <input
                    type="email"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white text-sm placeholder-white/20 focus:outline-none focus:border-purple-500 transition"
                    placeholder="you@example.com"
                    required
                    data-testid="forgot-email-input"
                  />
                  <button
                    type="submit"
                    disabled={forgotLoading}
                    className="w-full bg-purple-500 hover:bg-purple-600 disabled:opacity-50 text-white font-semibold py-3 rounded-xl transition text-sm"
                    data-testid="forgot-submit-btn"
                  >
                    {forgotLoading ? "Sending..." : "Send Reset Link"}
                  </button>
                </form>
                <button
                  onClick={() => { setForgotMode(false); setForgotMsg(""); }}
                  className="text-white/40 hover:text-white/70 text-xs transition mt-3 block mx-auto"
                  data-testid="forgot-back-btn"
                >
                  Back to login
                </button>
              </div>
            </div>
          )}

          <div className="flex items-center justify-center gap-1.5 mt-4">
            <Shield className="w-3 h-3 text-white/15" />
            <span className="text-white/15 text-[10px]">Protected by Cloudflare</span>
          </div>

          <p className="text-center text-white/50 text-sm mt-5">
            {mode === "login" ? (
              <>Don't have an account?{" "}
                <button onClick={() => switchMode("register")} className="text-purple-400 hover:text-purple-300 transition font-medium" data-testid="switch-to-register">
                  Sign up
                </button>
              </>
            ) : (
              <>Already have an account?{" "}
                <button onClick={() => switchMode("login")} className="text-purple-400 hover:text-purple-300 transition font-medium" data-testid="switch-to-login">
                  Sign in
                </button>
              </>
            )}
          </p>

          <p className="text-center text-white/15 text-xs mt-8">
            <Radio className="w-3 h-3 inline mr-1" />
            DJsandMCMedia™ — Your Channel. Your Brand. Your Platform.
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
